import { PROPERTY_STATUS, type PropertyStatus } from '../../constants/propertyStatus'
import type {
  OfferingCardWire,
  OfferingsListWireResponseDto,
  OfferingsPaginationDto,
  PropertyApiItem,
  PropertyFilterOptionsWireDto,
} from '../contracts/propertyContracts'
import type {
  OfferingFigures,
  PropertyListItem,
  PropertyStat,
  PropertyFilterOptions,
} from '../../types/propertyTypes'
import { daysUntilFuture, daysFromNowUntil } from '@shared/utils'
import { formatPropertyAddress } from '../../utils/propertyAddressUtils'
import {
  buildCardImageUrls,
  buildStatChip,
  computeTokensLeft,
  formatShortCount,
} from './propertyCardParser'

// Relative media paths resolve against the investor service; absolute URLs
// (the norm today) pass through untouched.
const IMAGE_BASE_URL: string | undefined = import.meta.env.VITE_INVESTOR_API_URL

// The Sliceal price is the API `purchasePrice` — the price the backend's
// `grossYieldPercentage` is computed from, so the ⓘ formulas reconcile.
// Deliberately diverges from investor-app, which still reads `listingPrice`.
const slicealPriceOf = (wire: OfferingCardWire): string | null =>
  wire.purchasePrice != null && wire.purchasePrice !== '' ? String(wire.purchasePrice) : null

// Bridge the camelCase wire row to the snake_case view-model the stat
// builders / address helper already consume. Keeping the bridge here lets
// the rest of the parser stay unchanged after the backend's camelCase swap.
const toPropertyApiItem = (wire: OfferingCardWire): PropertyApiItem => ({
  id: wire.id,
  built_up_area_sqm: wire.builtUpAreaSqm,
  property_type: wire.propertyType,
  number_of_bedrooms: wire.numberOfBedrooms,
  number_of_bathrooms: wire.numberOfBathrooms,
  state_province: null,
  city: wire.city,
  address_line_1: wire.addressLine1,
  address_line_2: wire.addressLine2,
  file_path: wire.filePath,
  offering_title: wire.offeringTitle,
  offering_description: wire.offeringDescription,
  offering_status: wire.offeringStatus,
  investment_window_start: wire.investmentWindowStart,
  investment_window_end: wire.investmentWindowEnd,
  listing_price: wire.listingPrice != null ? String(wire.listingPrice) : null,
  purchase_price: slicealPriceOf(wire),
  annual_rental_income: wire.annualRentalIncome != null ? String(wire.annualRentalIncome) : null,
  total_raised: wire.totalRaised != null ? String(wire.totalRaised) : null,
  total_tokens: wire.totalTokens,
  funded_percentage: wire.fundedPercentage,
  target_roi_5yr: wire.targetRoi5yr != null ? String(wire.targetRoi5yr) : null,
  token_price: wire.tokenPrice != null ? String(wire.tokenPrice) : null,
  expected_capital_appreciation:
    wire.expectedCapitalAppreciation != null ? String(wire.expectedCapitalAppreciation) : null,
  is_bookmarked: wire.isBookmarked,
  offering_bookmarks_id: wire.offeringBookmarksId != null ? String(wire.offeringBookmarksId) : null,
  is_notify_subscribed: wire.isNotifySubscribed ?? null,
  property_status: wire.propertyStatus,
  term: wire.term ?? null,
  media: wire.media ?? null,
  tokens_available: wire.tokensAvailable ?? null,
  total_investors: wire.totalInvestors ?? null,
  current_market_value: wire.currentMarketValue != null ? String(wire.currentMarketValue) : null,
  gross_yield_percentage:
    wire.grossYieldPercentage != null ? String(wire.grossYieldPercentage) : null,
  annual_roi_percentage: wire.annualRoiPercentage != null ? String(wire.annualRoiPercentage) : null,
  days_to_launch: wire.daysToLaunch,
})

const CLOSING_SOON_THRESHOLD_DAYS = 7
const HOT_FUNDING_THRESHOLD_PERCENT = 80
const FULL_FUNDING_PERCENT = 100
// Token prices below this are treated as data hazards and surfaced as
// "Pricing pending" — investor-trust signal beats raw fidelity.
const MIN_VIABLE_TOKEN_PRICE_AED = 0.01

const formatCurrency = (value: string | number | null | undefined, allowZero = false) => {
  const numericValue = Number(value ?? 0)
  if (!Number.isFinite(numericValue)) return undefined
  if (!allowZero && numericValue <= 0) return undefined

  return `AED ${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(numericValue)}`
}

const formatLabel = (value: string | null | undefined) =>
  value
    ?.split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ') || undefined

// Surface urgency, not a raw date: "Closes in 3 days" reads at a glance where
// "Closes 19 Jun" forces the investor to do the mental math. Mirrors the
// "Launches in X days" sibling chip and the detail page's closing chip.
const formatClosingLabel = (dateString: string | null | undefined) => {
  const daysLeft = daysFromNowUntil(dateString)
  if (daysLeft == null) return undefined
  if (daysLeft === 0) return 'Closes today'
  return `Closes in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`
}

const formatLaunchLabel = (dateString: string | null | undefined) => {
  if (!dateString) return undefined
  const launchDate = new Date(dateString)
  if (Number.isNaN(launchDate.getTime())) return undefined
  return `Launches ${launchDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
}

// The backend's `daysToLaunch` is the source of truth (it matches the detail
// page); the window-start date is only a fallback for payloads without it.
const calculateLaunchDaysLeft = (item: PropertyApiItem) => {
  if (item.days_to_launch != null && item.days_to_launch > 0) return item.days_to_launch
  return daysUntilFuture(item.investment_window_start) ?? undefined
}

const isClosingSoon = (dateString: string | null | undefined): boolean => {
  const daysLeft = daysFromNowUntil(dateString)
  return daysLeft !== null && daysLeft <= CLOSING_SOON_THRESHOLD_DAYS
}

const formatLocation = (item: PropertyApiItem) =>
  formatPropertyAddress({
    addressLine2: item.address_line_2,
    city: item.city,
    stateProvince: item.state_province,
  })

const formatPercent = (value: string | null | undefined) => {
  const numericValue = Number(value ?? 0)
  if (!Number.isFinite(numericValue) || numericValue === 0) return undefined
  return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(2)}%`
}

const formatArea = (item: PropertyApiItem): string | undefined =>
  item.built_up_area_sqm != null
    ? `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(item.built_up_area_sqm)} sqm`
    : undefined

// Funding progress comes straight from the backend, which measures it in
// TOKENS (tokens_sold ÷ total_tokens). Don't recompute from total_raised /
// listing_price on the client: total_raised accrues at a 2-dp-rounded
// token_price that doesn't reconcile with listing_price, so the money ratio
// overshoots 100%. Clamp defensively.
const computeFundingProgress = (item: PropertyApiItem): number =>
  Math.min(FULL_FUNDING_PERCENT, Math.max(0, Math.round(item.funded_percentage ?? 0)))

const compactStats = (entries: (PropertyStat | null)[]): PropertyStat[] =>
  entries.filter((stat): stat is PropertyStat => Boolean(stat))

// Per-status stats lists. Each builder returns the highlight tiles shown on
// the property card under the title/funding row.

const buildLiveStats = (item: PropertyApiItem): PropertyStat[] =>
  compactStats([
    // Label names the 5-year window explicitly. "Target ROI 52%" without a
    // timeframe reads as unrealistic / borderline non-compliant; "Projected
    // 5-yr ROI 52%" frames 52% as ~10%/year, which is credible for UAE
    // fractional real-estate and aligns with VARA/DLD presentation norms.
    item.target_roi_5yr
      ? {
          label: 'Projected 5-yr ROI',
          value: `${Number(item.target_roi_5yr).toFixed(1)}%`,
          highlight: true,
        }
      : null,
    item.token_price ? { label: 'Per Token', value: formatCurrency(item.token_price) ?? '' } : null,
    item.expected_capital_appreciation
      ? {
          label: 'Est. Appreciation',
          value: `${Number(item.expected_capital_appreciation).toFixed(1)}%`,
        }
      : null,
    item.term ? { label: 'Term', value: item.term } : null,
  ])

const buildLaunchingSoonStats = (item: PropertyApiItem): PropertyStat[] =>
  compactStats([
    item.target_roi_5yr
      ? {
          label: 'Projected 5-yr ROI',
          value: `${Number(item.target_roi_5yr).toFixed(1)}%`,
          highlight: true,
        }
      : null,
    item.token_price
      ? { label: 'Min Token Price', value: formatCurrency(item.token_price) ?? '' }
      : null,
  ])

const buildFundedStats = (item: PropertyApiItem): PropertyStat[] =>
  compactStats([
    item.target_roi_5yr
      ? {
          label: 'Projected 5-yr ROI',
          value: `${Number(item.target_roi_5yr).toFixed(1)}%`,
          highlight: true,
        }
      : null,
    item.expected_capital_appreciation
      ? {
          label: 'Gross Yield',
          value: `${Number(item.expected_capital_appreciation).toFixed(1)}%`,
        }
      : null,
    item.listing_price
      ? { label: 'Listing Price', value: formatCurrency(item.listing_price) ?? '' }
      : null,
    item.listing_price
      ? { label: 'Market Price', value: formatCurrency(item.listing_price) ?? '' }
      : null,
  ])

const STATS_BUILDERS: Record<PropertyStatus, (item: PropertyApiItem) => PropertyStat[]> = {
  [PROPERTY_STATUS.LIVE]: buildLiveStats,
  [PROPERTY_STATUS.LAUNCHING_SOON]: buildLaunchingSoonStats,
  [PROPERTY_STATUS.FULLY_FUNDED]: buildFundedStats,
}

// Per-status view-model fields that the card surfaces alongside the stats row
// (funding progress / launch countdown / fully-funded badges).

type StatusExtras = Partial<PropertyListItem>

const buildLiveExtras = (item: PropertyApiItem, fundingProgress: number): StatusExtras => ({
  closingLabel: formatClosingLabel(item.investment_window_end),
  closingSoon: isClosingSoon(item.investment_window_end),
  fundingCurrent: formatCurrency(item.total_raised, true),
  fundingTotal: formatCurrency(item.listing_price),
  fundingProgress,
  isHot: fundingProgress >= HOT_FUNDING_THRESHOLD_PERCENT,
})

const buildLaunchingSoonExtras = (item: PropertyApiItem): StatusExtras => ({
  launchDate: formatLaunchLabel(item.investment_window_start),
  launchDaysLeft: calculateLaunchDaysLeft(item),
})

const buildFundedExtras = (item: PropertyApiItem): StatusExtras => ({
  isRented: false,
  fundedInLabel: 'Fully Funded',
  fundingTotal: formatCurrency(item.listing_price),
  marketPriceChange: formatPercent(item.expected_capital_appreciation),
})

const EXTRAS_BUILDERS: Record<
  PropertyStatus,
  (item: PropertyApiItem, fundingProgress: number) => StatusExtras
> = {
  [PROPERTY_STATUS.LIVE]: buildLiveExtras,
  [PROPERTY_STATUS.LAUNCHING_SOON]: (item) => buildLaunchingSoonExtras(item),
  [PROPERTY_STATUS.FULLY_FUNDED]: (item) => buildFundedExtras(item),
}

// Card and list show returns to two decimals ("8.00%", "15.59%").
const PERCENT_DECIMALS = 2

const formatProjectedRoi = (raw: string | null | undefined): string | undefined => {
  if (raw == null || raw === '') return undefined
  const value = Number(raw)
  return Number.isFinite(value) ? `${value.toFixed(PERCENT_DECIMALS)}%` : undefined
}

// Strip the redundant "- Primary Offering" tail on offering_title — the user
// already knows they're on the primary-market listing surface; the suffix
// just steals title space.
const TITLE_SUFFIX_RE = /\s*(?:[-–—]\s*)?primary offering\s*$/i

const sanitiseTitle = (raw: string | null | undefined): string => {
  if (!raw) return 'Property offering'
  return raw.replace(TITLE_SUFFIX_RE, '').trim() || 'Property offering'
}

// Normalize copy that occasionally arrives with backslash-escaped percent signs
// or stray whitespace (LaTeX-style "5\%" or doubled spaces). Render-time fixes
// avoid surfacing API quirks as visible typos.
const sanitiseDescription = (raw: string | null | undefined): string | undefined => {
  const trimmed = raw?.trim()
  if (!trimmed) return undefined
  return trimmed.replace(/\\%/g, '%').replace(/\s+/g, ' ')
}

const YIELD_YEARS = 5
// Annual-yield guardrails — anything outside this band is a sign that the
// inputs don't follow the assumption (both numbers are 5-yr totals).
const ANNUAL_YIELD_MIN_PCT = 0.1
const ANNUAL_YIELD_MAX_PCT = 25

// Derive annual rental yield from the 5-yr totals on the DTO:
//   annualYield = (totalRoi5yr − capitalAppreciation5yr) / 5
// Suppressed when the result lands outside a defensible band — the alternative
// is a misleading number that erodes investor trust precisely where the brief
// says it matters most.
const deriveAnnualYield = (
  total5yrRaw: string | null | undefined,
  appreciationRaw: string | null | undefined,
): number | undefined => {
  const total = Number(total5yrRaw ?? 0)
  const appreciation = Number(appreciationRaw ?? 0)
  if (!Number.isFinite(total) || !Number.isFinite(appreciation)) return undefined
  const annual = (total - appreciation) / YIELD_YEARS
  if (annual < ANNUAL_YIELD_MIN_PCT || annual > ANNUAL_YIELD_MAX_PCT) return undefined
  return annual
}

// A positive rate sent by the backend (gross yield, annual ROI).
const parseRate = (raw: string | null | undefined): number | undefined => {
  if (raw == null || raw === '') return undefined
  const value = Number(raw)
  return Number.isFinite(value) && value > 0 ? value : undefined
}

const toPercentLabel = (value: number | undefined): string | undefined =>
  value == null ? undefined : `${value.toFixed(PERCENT_DECIMALS)}%`

interface ReturnBreakdown {
  // Backend gross yield only — dashboard featured-property card.
  rentalYieldLabel?: string
  // Gross yield, falling back to the derived yield — browse card / list.
  annualYieldLabel?: string
  annualRoiLabel?: string
}

// Rental yield / annual ROI labels. Annual ROI is the backend's figure only —
// never estimated, so a missing rate shows "-" rather than an invented number.
// Rental yield prefers the backend's gross yield and falls back to the 5-yr
// derivation. The browse card's ⓘ figures come from buildOfferingFigures.
const buildReturnBreakdown = (item: PropertyApiItem): ReturnBreakdown => {
  const backendYield = parseRate(item.gross_yield_percentage)
  const annualRoi = parseRate(item.annual_roi_percentage)
  const annualYield =
    backendYield ?? deriveAnnualYield(item.target_roi_5yr, item.expected_capital_appreciation)
  return {
    rentalYieldLabel: toPercentLabel(backendYield),
    annualYieldLabel: toPercentLabel(annualYield),
    annualRoiLabel: toPercentLabel(annualRoi),
  }
}

// Thresholds for the compact "AED Xk / Xm" formatter.
const COMPACT_MILLION = 1_000_000
const COMPACT_TEN_MILLION = 10_000_000
const COMPACT_THOUSAND = 1_000

// "540K" / "1.2M" / "30" — no currency, used for token counts.
const formatCompactNumber = (n: number): string => {
  if (n >= COMPACT_MILLION) {
    const digits = n >= COMPACT_TEN_MILLION ? 0 : 1
    return `${(n / COMPACT_MILLION).toFixed(digits)}M`
  }
  if (n >= COMPACT_THOUSAND) return `${Math.round(n / COMPACT_THOUSAND)}K`
  return String(Math.round(n))
}

const formatCompactCurrency = (value: string | number | null | undefined): string | undefined => {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n) || n <= 0) return undefined
  return `AED ${formatCompactNumber(n)}`
}

interface TokenAvailability {
  tokensLeftCompact?: string
  totalTokensCompact?: string
  hasTokensLeft?: boolean
}

// Same tokens-left figure the card's stat chip shows (computeTokensLeft), so
// the list view and the card never disagree.
const buildTokenAvailability = (
  tokensLeft: number | undefined,
  totalTokens: number,
): TokenAvailability => {
  if (tokensLeft == null || !Number.isFinite(totalTokens) || totalTokens <= 0) return {}
  return {
    tokensLeftCompact: formatShortCount(tokensLeft),
    totalTokensCompact: formatShortCount(totalTokens),
    hasTokensLeft: tokensLeft > 0,
  }
}

// Label MUST mirror the bar fill: token-based funding progress (sold ÷ total
// tokens), never a money ratio. Recomputing from total_raised / listing_price
// overshoots — total_raised accrues at a 2-dp-rounded token_price that doesn't
// reconcile with listing_price (see computeFundingProgress above and the
// backend's tokenFundedPercentage doc). The money ratio made the card label
// (e.g. "67% funded") disagree with both its own bar fill and the detail page,
// which are token-based (e.g. 40%).
const formatFundingPercent = (fundingProgress: number): string | undefined => {
  if (!Number.isFinite(fundingProgress) || fundingProgress <= 0) return undefined
  return `${fundingProgress}% funded`
}

const formatDaysRemaining = (days: number | null): string | undefined => {
  if (days == null || days < 0) return undefined
  if (days === 0) return 'Closes today'
  if (days === 1) return '1 day remaining'
  return `${days} days remaining`
}

// Photo header + token figures for the card (gallery, stat chip), the list
// view's funding column and the dashboard's featured-property media — one
// tokens-left source (computeTokensLeft) for all of them.
const buildCardHeader = (item: PropertyApiItem, status: PropertyStatus) => {
  const images = buildCardImageUrls(item.media, item.file_path, IMAGE_BASE_URL)
  const totalTokens = Number(item.total_tokens ?? 0)
  const tokensLeft = computeTokensLeft({
    totalTokens: item.total_tokens,
    fundedPercentage: item.funded_percentage,
    tokensAvailable: item.tokens_available,
  })
  return {
    // Cover only — the list-view thumbnail. The card renders an inline icon
    // placeholder when empty or when the image fails to load.
    image: images[0] ?? '',
    images,
    statChip: buildStatChip(status, {
      totalTokens: item.total_tokens,
      tokensLeft,
      totalInvestors: item.total_investors,
    }),
    // "30 out of 540K Tokens Left" — dashboard featured-property media.
    tokensLeftLabel:
      tokensLeft != null && totalTokens > 0
        ? `${formatShortCount(tokensLeft)} out of ${formatShortCount(totalTokens)} Tokens Left`
        : undefined,
    ...buildTokenAvailability(tokensLeft, totalTokens),
  }
}

// Discount of the Sliceal (listing) price against the current valuation.
// Floored to one decimal and dropped under 1% — an offering priced at or over
// its valuation has no saving to advertise. Mirrors investor-app's
// `statedDiscountPct` so the two surfaces agree.
const MIN_DISCOUNT_PCT = 1

const floorToTenth = (value: number): number => Math.floor(value * 10) / 10

interface MarketPricing {
  marketPriceLabel?: string
  // "9.1% off" — dashboard featured-property card.
  marketDiscountLabel?: string
}

const buildMarketPricing = (item: PropertyApiItem): MarketPricing => {
  const marketPriceLabel = formatCurrency(item.current_market_value)
  const listing = Number(item.listing_price ?? 0)
  const market = Number(item.current_market_value ?? 0)
  if (!marketPriceLabel || !Number.isFinite(listing) || listing <= 0 || !(market > listing)) {
    return { marketPriceLabel }
  }
  const discountPct = ((market - listing) / market) * 100
  if (discountPct < MIN_DISCOUNT_PCT) return { marketPriceLabel }
  return { marketPriceLabel, marketDiscountLabel: `${floorToTenth(discountPct)}% off` }
}

const toFiniteNumber = (raw: string | null | undefined): number | undefined => {
  if (raw == null || raw === '') return undefined
  const value = Number(raw)
  return Number.isFinite(value) ? value : undefined
}

// Inputs for the card ⓘ builders (constants/offeringInfo.ts), named as in
// the mobile app. The Sliceal price is the API `purchasePrice` (see
// slicealPriceOf); discount is derived from it against the market value, and appreciation
// comes from `expectedCapitalAppreciation`.
const buildOfferingFigures = (item: PropertyApiItem): OfferingFigures => {
  const listing = toFiniteNumber(item.listing_price)
  const purchase = toFiniteNumber(item.purchase_price)
  const market = toFiniteNumber(item.current_market_value)
  return {
    listingPrice: formatCurrency(item.listing_price),
    purchasePrice: formatCurrency(item.purchase_price),
    marketValue: formatCurrency(item.current_market_value),
    currentMarketValue: market,
    fundingTotal: listing,
    discountPct:
      purchase != null && purchase > 0 && market != null && market > 0
        ? ((market - purchase) / market) * 100
        : undefined,
    grossYieldPct: toFiniteNumber(item.gross_yield_percentage),
    annualRentalIncome: toFiniteNumber(item.annual_rental_income),
    annualRoiPct: toFiniteNumber(item.annual_roi_percentage),
    appreciationPct: toFiniteNumber(item.expected_capital_appreciation),
  }
}

export const parsePropertyListItem = (
  item: PropertyApiItem,
  status: PropertyStatus,
): PropertyListItem => {
  const fundingProgress = computeFundingProgress(item)
  const tokenPriceAmount = Number(item.token_price ?? 0)
  const raisedAmount = Number(item.total_raised ?? 0)
  // Funding goal is the fully-funded raise — total_tokens × token_price — NOT
  // listing_price. total_raised accrues at token_price, so the "X of Y raised"
  // line must measure against the same token economics; using listing_price
  // (which need not equal total_tokens × token_price) made raised overshoot the
  // total, e.g. "AED 9K of AED 6K raised". This mirrors the detail page, where
  // raised + available = total_tokens × token_price.
  const fundingGoalAmount = tokenPriceAmount * Number(item.total_tokens ?? 0)
  const daysRemaining = daysFromNowUntil(item.investment_window_end)

  const tokenPriceViable =
    Number.isFinite(tokenPriceAmount) && tokenPriceAmount >= MIN_VIABLE_TOKEN_PRICE_AED

  return {
    id: item.id,
    figures: buildOfferingFigures(item),
    ...buildCardHeader(item, status),
    imageAlt: item.offering_title || 'Property image',
    location: formatLocation(item),
    title: sanitiseTitle(item.offering_title),
    description: sanitiseDescription(item.offering_description),
    tokenPriceLabel: tokenPriceViable ? formatCurrency(item.token_price) : undefined,
    tokenPriceAmount: tokenPriceViable ? tokenPriceAmount : undefined,
    listingPriceLabel: formatCurrency(item.listing_price),
    ...buildMarketPricing(item),
    projectedRoiLabel: formatProjectedRoi(item.target_roi_5yr),
    capitalAppreciationLabel: formatProjectedRoi(item.expected_capital_appreciation),
    ...buildReturnBreakdown(item),
    fundingPercentLabel: formatFundingPercent(fundingProgress),
    fundingRaisedCompact: formatCompactCurrency(raisedAmount),
    fundingTotalCompact: formatCompactCurrency(fundingGoalAmount),
    fundingDaysRemainingLabel: formatDaysRemaining(daysRemaining),
    term: item.term ?? undefined,
    closingDaysLeft: daysRemaining ?? undefined,
    isBookmarked: item.is_bookmarked ?? false,
    isNotifySubscribed: item.is_notify_subscribed ?? false,
    isPremium: false,
    area: formatArea(item),
    propertyType: formatLabel(item.property_type),
    bedrooms: item.number_of_bedrooms ?? undefined,
    bathrooms: item.number_of_bathrooms ?? undefined,
    stats: STATS_BUILDERS[status](item),
    ...EXTRAS_BUILDERS[status](item, fundingProgress),
  }
}

export interface ParsedPropertyList {
  rows: PropertyListItem[]
  pagination: OfferingsPaginationDto
}

export const parsePropertyList = (
  resp: OfferingsListWireResponseDto,
  status: PropertyStatus,
): ParsedPropertyList => {
  const wireRows = resp.data ?? []
  const pagination: OfferingsPaginationDto = resp.pagination ?? {
    page: 1,
    limit: 12,
    totalItems: 0,
    totalPages: 0,
  }
  return {
    rows: wireRows.map((wire) => parsePropertyListItem(toPropertyApiItem(wire), status)),
    pagination,
  }
}

export const parsePropertyFilters = (
  resp: PropertyFilterOptionsWireDto,
): PropertyFilterOptions => ({
  locations: resp.data?.locations ?? [],
  propertyTypes: resp.data?.propertyTypes ?? [],
  roi: resp.data?.roi ?? [],
  sortBy: resp.data?.sortBy ?? [],
})
